<script>
import LayoutMain from "@/router/layouts/main";
import LayoutExperts from "@/router/layouts/experts";
import { api } from "@/state/services";
import ExpertsPlanCard from "@/components/widgets/experts-plan-card";

export default {
  components: {
    LayoutMain,
    LayoutExperts,
    ExpertsPlanCard,
  },
  data() {
    return {
      loading: true,
      errored: false,

      plans: [],
      plan: null,
      pending: null,

      timer: null,
    };
  },
  computed: {
    // Sem plano do Experts ativo usa o layout sem menu; com plano ativo mantém o menu
    layout() {
      const user = this.$store.state.account.user;
      return user && user.expert == 'active' ? 'LayoutMain' : 'LayoutExperts';
    },
    firstName() {
      const user = this.$store.state.account.user;
      if (!user || !user.name) {
        return '';
      }
      const name = user.name.split(' ')[0].toLowerCase();
      return name.charAt(0).toUpperCase() + name.slice(1);
    },
    // Planos que o usuário pode contratar (todos, ou apenas os superiores ao atual)
    availablePlans() {
      if (!this.plan) {
        return this.plans;
      }
      return this.plans.filter(row => row.type > this.plan.type);
    },
    // Planos disponíveis para trocar a fatura pendente
    changePlans() {
      if (!this.pending) {
        return [];
      }
      return this.availablePlans.filter(row => row.id != this.pending.plan.id);
    },
  },
  methods: {
    load(silent) {
      if (!silent) {
        this.loading = true;
      }
      this.errored = false;

      return Promise.all([api.get('experts'), api.get('experts/current')])
        .then(([plans, current]) => {
          if (plans.data.status == 'success') {
            this.plans = plans.data.list;
          }
          if (current.data.status == 'success') {
            const hadPlan = !!this.plan;

            this.plan = current.data.plan;
            this.pending = current.data.pending;

            // Plano ativado: atualiza o usuário para liberar o menu
            // (só nas consultas automáticas: no carregamento da página o plano já estava ativo)
            if (silent && !hadPlan && this.plan) {
              this.refreshUser();
            }
          }
          this.schedule();
        })
        .catch(() => {
          if (silent) {
            // Falha numa consulta automática: tenta de novo no próximo ciclo
            this.schedule();
          } else {
            this.errored = true;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Com fatura em aberto, consulta a situação: a cada 5s na ativação e a cada 15s aguardando o pagamento
    schedule() {
      clearTimeout(this.timer);
      if (this.pending) {
        const delay = this.pending.status == 'processing' ? 5000 : 15000;
        this.timer = setTimeout(() => this.load(true), delay);
      }
    },
    refreshUser() {
      api.get('user').then(response => {
        if (response.data && response.data.status == 'success') {
          this.$store.state.account.user = response.data.user;
          this.$toast.success('Seu plano foi ativado com sucesso!');
        }
      });
    },
    // Abre a tela de confirmação do plano escolhido
    showConfirm(plan) {
      this.$router.push('/experts/confirm/' + plan.id);
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  },
};
</script>

<template>
  <component :is="layout">
    <!-- Plano ativo: título padrão das páginas do escritório virtual -->
    <div v-if="plan" class="page-title-box d-md-flex justify-content-between">
      <div>
        <h3>Meu Plano</h3>
        <p></p>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="default" role="status"></b-spinner>
    </div>
    <div v-else-if="errored" class="text-center py-5">
      <p>Pedimos desculpas, não podemos recuperar as informações neste momento.</p>
      <button class="btn btn-default" v-on:click="load()">Tentar novamente</button>
    </div>
    <div v-else class="experts">
      <!-- Chamada -->
      <div v-if="!plan" class="experts-intro">
        <h1 class="experts-title">
          Olá<template v-if="firstName">, {{ firstName }}</template>!
          <template v-if="pending">Falta pouco para ativar seu plano.</template>
          <template v-else>Ative seu plano anual.</template>
        </h1>
        <p v-if="!plan && !pending" class="experts-subtitle">
          Escolha o plano ideal para você e libere todos os benefícios da plataforma por 12 meses.
        </p>
        <ul v-if="!plan && !pending" class="experts-highlights">
          <li><i class="bx bx-bolt-circle" aria-hidden="true"></i> Ativação em minutos</li>
          <li><i class="bx bx-credit-card" aria-hidden="true"></i> PIX ou cartão</li>
          <li><i class="bx bx-calendar-check" aria-hidden="true"></i> Válido por 12 meses</li>
        </ul>
      </div>


      <!-- Plano próximo do vencimento -->
      <div v-if="plan && plan.expiring == 'yes'" class="alert alert-warning experts-box p-3">
        Seu plano vence em {{ plan.due_date }}. Após o vencimento você volta ao plano básico e poderá contratar novamente.
      </div>

      <!-- Fatura pendente -->
      <div v-if="pending" class="experts-box">
        <div class="d-flex justify-content-between align-items-start mb-2">
          <h5 class="mb-0">Fatura {{ pending.status == 'processing' ? 'em ativação' : 'aguardando pagamento' }}</h5>
          <span v-if="pending.status == 'processing'" class="badge badge-soft-success font-size-12">PAGO</span>
          <span v-else class="badge badge-soft-danger font-size-12">PENDENTE</span>
        </div>
        <p class="mb-1"><strong>{{ pending.plan.name }}</strong> · 12 meses</p>
        <h3 class="text-default mb-2">{{ pending.total | currency }}</h3>
        <template v-if="pending.status == 'processing'">
          <p class="text-muted mb-0">
            <b-spinner small class="mr-1 align-middle" variant="default" role="status"></b-spinner>
            Recebemos o seu pagamento e estamos ativando o plano. Isso leva poucos instantes.
          </p>
        </template>
        <template v-else>
          <p class="text-muted">Esta fatura vence em {{ pending.expires }}.</p>
          <router-link tag="a" :to="'/experts/order/' + pending.order.id" class="btn btn-default btn-lg btn-block text-uppercase mb-3">
            Pagar agora
          </router-link>
          <div class="row">
            <div class="col-12 col-sm-6 mb-2" v-if="changePlans.length > 0">
              <router-link tag="a" to="/experts/change" class="btn btn-outline-default btn-lg btn-block text-uppercase">Trocar plano</router-link>
            </div>
            <div class="col-12 col-sm-6 mb-2">
              <router-link tag="a" to="/experts/cancel" class="btn btn-outline-danger btn-lg btn-block text-uppercase">Cancelar fatura</router-link>
            </div>
          </div>
        </template>
      </div>

      <!-- Plano ativo -->
      <div v-if="plan" class="experts-box">
        <div class="d-flex justify-content-between align-items-start mb-2">
          <h5 class="mb-0">Seu plano</h5>
          <span class="badge badge-soft-success font-size-12">ATIVO</span>
        </div>
        <h3 class="text-default mb-1">{{ plan.name }}</h3>
        <p class="text-muted mb-0">Válido até {{ plan.due_date }} ({{ plan.days_left }} dias restantes).</p>
      </div>

      <!-- Planos para contratar ou fazer upgrade -->
      <div v-if="!pending && availablePlans.length > 0" class="mt-4">
        <h2 class="experts-pill">
          <span>{{ plan ? 'FAÇA UPGRADE' : 'ESCOLHA SEU' }}</span>
          <strong>PLANO ANUAL</strong>
        </h2>
        <div class="experts-plans" :class="{ 'is-single': availablePlans.length == 1 }">
          <ExpertsPlanCard
            v-for="item in availablePlans"
            :key="item.id"
            :plan="item"
            :recommended="availablePlans.length > 1 && item.type == 3"
            :action="(plan ? 'Fazer upgrade para ' : 'Quero ser ') + item.name"
            @choose="showConfirm($event)"
          />
        </div>

        <p class="experts-footnote">
          <span><i class="bx bx-lock-alt" aria-hidden="true"></i> Pagamento seguro</span>
          <span><i class="bx bx-credit-card" aria-hidden="true"></i> PIX ou cartão de crédito</span>
          <span><i class="bx bx-time-five" aria-hidden="true"></i> Ativação em minutos após o pagamento</span>
        </p>
      </div>

      <!-- Perguntas frequentes -->
      <div v-if="!plan" class="experts-faq">
        <h5>Perguntas frequentes</h5>
        <details>
          <summary>Em quanto tempo meu plano é ativado?</summary>
          <p>Logo após a confirmação do pagamento. No PIX e no cartão a ativação acontece em poucos minutos.</p>
        </details>
        <details>
          <summary>Por quanto tempo o plano vale?</summary>
          <p>12 meses a partir da confirmação do pagamento. Ao vencer, você volta ao plano básico e pode contratar novamente quando quiser.</p>
        </details>
        <details>
          <summary>Posso trocar de plano depois?</summary>
          <p>Sim. Com o plano ativo você pode fazer upgrade para um plano superior; a nova vigência de 12 meses começa na confirmação do pagamento.</p>
        </details>
        <details>
          <summary>Gerei a fatura e quero mudar o plano. E agora?</summary>
          <p>Enquanto a fatura não for paga, você pode trocar o plano ou cancelar a fatura nesta página. A fatura expira automaticamente em 3 dias.</p>
        </details>
      </div>
    </div>
  </component>
</template>
